import React, { useEffect, useMemo, useState } from 'react';
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Button } from '../ui/button';
import { api } from '../../context/AuthContext';
import { DataTable, Loading, Section, Segmented, StatGroup, StatTile, usd } from './shared';

// Categorical slots 1-2 of the validated chart palette (light surface): identity, not status
const SERIES = { credit: '#2a78d6', cost: '#eb6834' };
const RANGES = [{ value: 7, label: '7 days' }, { value: 30, label: '30 days' }, { value: 90, label: '90 days' }];
const DIMENSIONS = [
  { value: 'by_feature', label: 'Feature' }, { value: 'by_model', label: 'Model' }, { value: 'by_provider', label: 'Provider' },
];
const seconds = (ms) => `${((ms || 0) / 1000).toFixed(1)}s`;

// One point per calendar day in the range (days without usage are 0), so the x-axis is true time
const fillDays = (rows, days) => {
  const byDay = Object.fromEntries((rows || []).map((r) => [r.key, r]));
  const out = [];
  const today = new Date();
  for (let i = days - 1; i >= 0; i -= 1) {
    const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - i));
    const key = d.toISOString().slice(0, 10);
    out.push({ day: key.slice(5), cost: byDay[key]?.cost_usd || 0, credit: byDay[key]?.credit_value_usd || 0 });
  }
  return out;
};

const BREAKDOWN_COLUMNS = (keyLabel) => [
  { key: 'key', label: keyLabel, className: 'max-w-[280px] truncate font-medium text-slate-800',
    render: (r) => <span title={r.key}>{r.key}</span> },
  { key: 'calls', label: 'Calls', align: 'right' },
  { key: 'failure_rate', label: 'Failed', align: 'right', render: (r) => `${r.failure_rate}%` },
  { key: 'fallback_rate', label: 'Fallback', align: 'right', render: (r) => `${r.fallback_rate}%` },
  { key: 'latency', label: 'p50 / p95', align: 'right', className: 'whitespace-nowrap text-slate-600',
    render: (r) => `${seconds(r.latency_p50_ms)} / ${seconds(r.latency_p95_ms)}` },
  { key: 'credits', label: 'Credits', align: 'right' },
  { key: 'cost_usd', label: 'Cost', align: 'right',
    render: (r) => <>{usd(r.cost_usd, 3)}{r.calls_without_cost > 0 && <span className="text-slate-400">*</span>}</> },
  { key: 'credit_value_usd', label: 'Credit value', align: 'right', render: (r) => usd(r.credit_value_usd, 3) },
  { key: 'margin_pct', label: 'Margin', align: 'right', render: (r) => (r.margin_pct == null ? '-' : `${r.margin_pct}%`) },
];

/** Spend vs revenue: provider cost against credit value (and Stripe revenue), by day/feature/provider/model. */
const OverviewTab = () => {
  const [days, setDays] = useState(30);
  const [dimension, setDimension] = useState('by_feature');
  const [report, setReport] = useState(null);
  const [showTable, setShowTable] = useState(false);

  useEffect(() => {
    setReport(null);
    api.get('/admin/ai/margins', { params: { days } }).then((res) => setReport(res.data)).catch(() => setReport({}));
  }, [days]);

  const daily = useMemo(() => fillDays(report?.by_day, days), [report, days]);

  if (!report) return <Loading />;
  const t = report.totals || {};
  const coverage = t.calls ? Math.round(((t.calls - t.calls_without_cost) / t.calls) * 100) : 100;
  const revenueParts = Object.entries(report.stripe_revenue || {}).map(([k, v]) => `${k.replace('_', ' ')} ${usd(v)}`);
  const dimensionLabel = DIMENSIONS.find((d) => d.value === dimension).label;

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Usage &amp; margins</h2>
          <p className="text-sm text-slate-500">All AI calls in the selected period.</p>
        </div>
        <Segmented label="Date range" options={RANGES} value={days} onChange={setDays} />
      </div>

      <StatGroup title="Money">
        <StatTile label="Provider cost" value={usd(t.cost_usd)} hint={`${coverage}% of calls have cost data`} />
        <StatTile label="Credit value" value={usd(t.credit_value_usd)}
          hint={`${(t.credits || 0).toLocaleString()} credits × ${usd(report.credit_value_usd_per_credit, 3)}`} />
        <StatTile label="Margin on credits" value={t.margin_pct == null ? '-' : `${t.margin_pct}%`} hint={usd(t.margin_usd)} />
        <StatTile label="Stripe revenue" value={usd(t.stripe_revenue_usd)} hint={revenueParts.join(' · ') || 'No payments recorded yet'} />
      </StatGroup>

      <StatGroup title="Reliability">
        <StatTile label="Successful calls" value={(t.calls || 0).toLocaleString()} />
        <StatTile label="Failure rate" value={`${t.failure_rate || 0}%`} hint={`${t.failed || 0} failed · ${t.blocked || 0} blocked`} />
        <StatTile label="Fallback rate" value={`${t.fallback_rate || 0}%`} hint="Calls answered by a backup provider" />
        <StatTile label="Latency p50" value={seconds(t.latency_p50_ms)} hint={`p95 ${seconds(t.latency_p95_ms)}`} />
      </StatGroup>

      <Section
        title="Daily provider cost vs credit value"
        description="USD per day. Credit value is credits charged × list price per credit."
        actions={<Button size="sm" variant="outline" onClick={() => setShowTable((v) => !v)}>{showTable ? 'Show chart' : 'Show table'}</Button>}
      >
        {showTable ? (
          <DataTable
            rowKey={(r) => r.day}
            rows={[...daily].reverse()}
            columns={[
              { key: 'day', label: 'Day' },
              { key: 'cost', label: 'Provider cost', align: 'right', render: (r) => usd(r.cost, 3) },
              { key: 'credit', label: 'Credit value', align: 'right', render: (r) => usd(r.credit, 3) },
            ]}
          />
        ) : (
          <div className="h-72" data-testid="margin-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={daily} margin={{ top: 8, right: 16, bottom: 4, left: 4 }} barGap={2} barCategoryGap="20%">
                <CartesianGrid stroke="#e7e5e4" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 12, fill: '#57534e' }} tickLine={false}
                  axisLine={{ stroke: '#d6d3d1' }} interval="preserveStartEnd" minTickGap={24} />
                <YAxis tick={{ fontSize: 12, fill: '#57534e' }} tickLine={false} axisLine={false} width={60}
                  tickFormatter={(v) => usd(v, v < 1 ? 2 : 0)} allowDecimals />
                <Tooltip formatter={(value, name) => [usd(value, 3), name]} labelFormatter={(l) => `Day ${l}`}
                  cursor={{ fill: '#f5f5f4' }} />
                <Legend verticalAlign="top" align="right" height={28} iconType="square" wrapperStyle={{ fontSize: 12, color: '#44403c' }} />
                <Bar dataKey="credit" name="Credit value" fill={SERIES.credit} radius={[4, 4, 0, 0]} maxBarSize={18} />
                <Bar dataKey="cost" name="Provider cost" fill={SERIES.cost} radius={[4, 4, 0, 0]} maxBarSize={18} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Section>

      <Section
        title="Breakdown"
        description="Calls, reliability, cost and margin per group."
        actions={<Segmented label="Group by" options={DIMENSIONS} value={dimension} onChange={setDimension} />}
        footer={<p className="text-xs text-slate-500">* Some calls in this row have no cost data (the provider didn&apos;t
          report it and no price is set), so cost is understated and margin overstated.</p>}
      >
        <DataTable rowKey={(r) => r.key} rows={report[dimension] || []} columns={BREAKDOWN_COLUMNS(dimensionLabel)}
          empty="No AI usage in this period" />
      </Section>
    </div>
  );
};

export default OverviewTab;
